import React from "react";

// Match backend LUDO_PATH order (52 main-track tiles, (row, col) on a 15x15 grid)
const PATH = [
  [6, 1], [6, 2], [6, 3], [6, 4], [6, 5],
  [5, 6], [4, 6], [3, 6], [2, 6], [1, 6], [0, 6],
  [0, 7], [0, 8],
  [1, 8], [2, 8], [3, 8], [4, 8], [5, 8],
  [6, 9], [6, 10], [6, 11], [6, 12], [6, 13],
  [6, 14], [7, 14], [8, 14],
  [8, 13], [8, 12], [8, 11], [8, 10], [8, 9],
  [9, 8], [10, 8], [11, 8], [12, 8], [13, 8], [14, 8],
  [14, 7], [14, 6],
  [13, 6], [12, 6], [11, 6], [10, 6], [9, 6],
  [8, 5], [8, 4], [8, 3], [8, 2], [8, 1],
  [8, 0], [7, 0], [6, 0],
];
const HOME_COLS = {
  0: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5]],
  1: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9]],
};
// Decorative home strips for Green (top) & Blue (bottom) — visual only, not used in 2p gameplay
const GREEN_STRIP = [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7]];
const BLUE_STRIP  = [[9, 7], [10, 7], [11, 7], [12, 7], [13, 7]];
// Start-tile arrow indicators (path index → arrow char)
const START_ARROW = {
  0:  { ch: "→", color: "text-red-600" },   // Red start (6,1) points right
  26: { ch: "←", color: "text-yellow-700" },// Yellow start (8,13) points left
};
const START_INDEX = { 0: 0, 1: 26 };
const SAFE_SQUARES = new Set([0, 8, 13, 21, 26, 34, 39, 47]);
const PERIMETER_LEN = 52;   // geometric perimeter (modulo wrap)
const MAIN_LEN = 51;        // each player's main-track travel before entering home
const FINISH_PROGRESS = MAIN_LEN + 5 + 1;  // 57

// Ludo King style colors
const SLOT_BASE_COLOR = { 0: "bg-red-500", 1: "bg-yellow-400" };
const SLOT_BASE_LIGHT = { 0: "bg-red-100", 1: "bg-yellow-100" };
const SLOT_BORDER = { 0: "border-red-700", 1: "border-yellow-700" };
const SLOT_TOKEN_COLOR = { 0: "bg-red-600", 1: "bg-yellow-500" };

function tileKey(r, c) { return `${r},${c}`; }

function buildTokenMap(tokens, slots) {
  const map = {};
  Object.entries(tokens || {}).forEach(([pid, arr]) => {
    const slot = slots?.[pid] ?? 0;
    arr.forEach((t, i) => {
      const p = t.progress;
      let rc = null;
      let finished = false;
      if (p >= FINISH_PROGRESS) finished = true;
      else if (p === 0) rc = PATH[START_INDEX[slot]];
      else if (p <= MAIN_LEN) rc = PATH[(START_INDEX[slot] + p - 1) % PERIMETER_LEN];
      else rc = HOME_COLS[slot][p - MAIN_LEN - 1];
      const token = { pid, slot, tokenIdx: i, progress: p, finished };
      if (rc) {
        const k = tileKey(rc[0], rc[1]);
        map[k] = map[k] || [];
        map[k].push(token);
      }
    });
  });
  return map;
}

// Check if (r,c) is inside a given quadrant's 6x6 home base
function inQuadrant(r, c, quadrant) {
  // quadrants: 'TL' (0-5,0-5) Red, 'TR' (0-5,9-14) Green, 'BL' (9-14,0-5) Blue, 'BR' (9-14,9-14) Yellow
  if (quadrant === 'TL') return r >= 0 && r <= 5 && c >= 0 && c <= 5;
  if (quadrant === 'TR') return r >= 0 && r <= 5 && c >= 9 && c <= 14;
  if (quadrant === 'BL') return r >= 9 && r <= 14 && c >= 0 && c <= 5;
  if (quadrant === 'BR') return r >= 9 && r <= 14 && c >= 9 && c <= 14;
  return false;
}

// Inner circle (4x4) of a quadrant where tokens sit when "in base"
function isBaseCircle(r, c, quadrant) {
  if (quadrant === 'TL') return r >= 1 && r <= 4 && c >= 1 && c <= 4;
  if (quadrant === 'TR') return r >= 1 && r <= 4 && c >= 10 && c <= 13;
  if (quadrant === 'BL') return r >= 10 && r <= 13 && c >= 1 && c <= 4;
  if (quadrant === 'BR') return r >= 10 && r <= 13 && c >= 10 && c <= 13;
  return false;
}

// The 4 "parking slots" inside each base — 4 colored circles at corners of the inner base area
function baseSlotColor(r, c) {
  // TL base (rows 1-4, cols 1-4) → 4 parking spots at the 4 inner corners
  if ((r === 1 && c === 1) || (r === 1 && c === 4) || (r === 4 && c === 1) || (r === 4 && c === 4)) return 'red';
  // TR base (rows 1-4, cols 10-13)
  if ((r === 1 && c === 10) || (r === 1 && c === 13) || (r === 4 && c === 10) || (r === 4 && c === 13)) return 'green';
  // BL base (rows 10-13, cols 1-4)
  if ((r === 10 && c === 1) || (r === 10 && c === 4) || (r === 13 && c === 1) || (r === 13 && c === 4)) return 'blue';
  // BR base (rows 10-13, cols 10-13)
  if ((r === 10 && c === 10) || (r === 10 && c === 13) || (r === 13 && c === 10) || (r === 13 && c === 13)) return 'yellow';
  return null;
}

function isPathTile(r, c, pathIndex) {
  return pathIndex !== -1;
}

function findPathIndex(r, c) {
  for (let i = 0; i < PATH.length; i++) {
    if (PATH[i][0] === r && PATH[i][1] === c) return i;
  }
  return -1;
}

function findHomeColumn(r, c) {
  for (const slot of [0, 1]) {
    for (let i = 0; i < HOME_COLS[slot].length; i++) {
      const [hr, hc] = HOME_COLS[slot][i];
      if (hr === r && hc === c) return { slot, index: i };
    }
  }
  return null;
}

export default function LudoBoard({ tokens, slots, myId, myMovable, onMoveToken, lastRoll }) {
  const tokenMap = buildTokenMap(tokens, slots);
  const mySlot = slots?.[myId] ?? 0;

  const cells = [];
  for (let r = 0; r < 15; r++) {
    for (let c = 0; c < 15; c++) cells.push([r, c]);
  }

  const renderCell = (r, c) => {
    const pi = findPathIndex(r, c);
    const hc = findHomeColumn(r, c);
    const key = tileKey(r, c);
    const here = tokenMap[key] || [];
    const isCenter = r >= 6 && r <= 8 && c >= 6 && c <= 8;
    const baseSlot = baseSlotColor(r, c);

    let cellClass = "relative flex items-center justify-center";
    let content = null;
    let overlay = null;

    // Add colored "parking slot" pins inside the 4 bases (Ludo King style — show empty token shapes)
    if (baseSlot) {
      const colorMap = {
        red:    { fill: "#dc2626", dark: "#7f1d1d" },
        green:  { fill: "#16a34a", dark: "#14532d" },
        blue:   { fill: "#2563eb", dark: "#1e3a8a" },
        yellow: { fill: "#eab308", dark: "#854d0e" },
      };
      const col = colorMap[baseSlot];
      overlay = (
        <svg viewBox="0 0 24 32" className="absolute inset-[5%] w-[90%] h-[90%] z-0 opacity-80 pointer-events-none" preserveAspectRatio="xMidYMid meet">
          <path
            d="M12 1 C5.5 1 1 5.5 1 12 C1 17 6 22 12 31 C18 22 23 17 23 12 C23 5.5 18.5 1 12 1 Z"
            fill={col.fill}
            stroke="white"
            strokeWidth="1.5"
          />
          <circle cx="12" cy="11" r="4" fill="white" />
          <circle cx="12" cy="11" r="2.8" fill={col.dark} opacity="0.35" />
        </svg>
      );
    }

    // Quadrant outer border tile (thick colored frame)
    const onOuterBorder = (qRowMin, qRowMax, qColMin, qColMax) =>
      (r === qRowMin || r === qRowMax || c === qColMin || c === qColMax);

    // Corner quadrants with 4-slot circular bases (Ludo King style)
    if (inQuadrant(r, c, 'TL')) {
      if (onOuterBorder(0, 5, 0, 5)) {
        cellClass += " bg-red-600";
      } else {
        const inCircle = isBaseCircle(r, c, 'TL');
        cellClass += inCircle ? " bg-white" : " bg-red-500";
        // Slot circles in corners of base
        if ((r === 2 && c === 2) || (r === 2 && c === 3) || (r === 3 && c === 2) || (r === 3 && c === 3)) {
          // already white
        }
      }
    } else if (inQuadrant(r, c, 'TR')) {
      if (onOuterBorder(0, 5, 9, 14)) cellClass += " bg-green-600";
      else cellClass += isBaseCircle(r, c, 'TR') ? " bg-white" : " bg-green-500";
    } else if (inQuadrant(r, c, 'BL')) {
      if (onOuterBorder(9, 14, 0, 5)) cellClass += " bg-blue-600";
      else cellClass += isBaseCircle(r, c, 'BL') ? " bg-white" : " bg-blue-500";
    } else if (inQuadrant(r, c, 'BR')) {
      if (onOuterBorder(9, 14, 9, 14)) cellClass += " bg-yellow-500";
      else cellClass += isBaseCircle(r, c, 'BR') ? " bg-white" : " bg-yellow-400";
    }
    // Path tile
    else if (pi !== -1) {
      cellClass += " bg-white border border-gray-300";
      if (pi === 0) cellClass = "relative flex items-center justify-center bg-red-400 border border-red-700";
      else if (pi === 26) cellClass = "relative flex items-center justify-center bg-yellow-300 border border-yellow-700";
      // Decorative Green (top) vertical home strip
      if (GREEN_STRIP.some(([gr, gc]) => gr === r && gc === c)) {
        cellClass = "relative flex items-center justify-center bg-green-500 border border-green-700";
      }
      // Decorative Blue (bottom) vertical home strip
      if (BLUE_STRIP.some(([br, bc]) => br === r && bc === c)) {
        cellClass = "relative flex items-center justify-center bg-blue-500 border border-blue-700";
      }
      // Start arrow indicators
      if (START_ARROW[pi]) {
        overlay = (
          <>
            {overlay}
            <span className={`absolute text-[14px] font-bold ${START_ARROW[pi].color}`}>
              {START_ARROW[pi].ch}
            </span>
          </>
        );
      }
      if (SAFE_SQUARES.has(pi)) {
        overlay = (
          <>
            {overlay}
            <span className="absolute text-[11px] text-gray-700 font-bold pointer-events-none">★</span>
          </>
        );
      }
    }
    // Home column colored cells
    else if (hc !== null) {
      if (hc.slot === 0) cellClass += " bg-red-400 border border-red-700";
      else cellClass += " bg-yellow-300 border border-yellow-600";
    }
    // Center triangles (home)
    else if (isCenter) {
      if (r === 7 && c === 7) cellClass += " bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 shadow-inner";
      else if (r === 6 && c === 7) cellClass += " bg-gradient-to-b from-green-500 to-green-600";
      else if (r === 7 && c === 6) cellClass += " bg-gradient-to-r from-red-500 to-red-600";
      else if (r === 8 && c === 7) cellClass += " bg-gradient-to-t from-blue-500 to-blue-600";
      else if (r === 7 && c === 8) cellClass += " bg-gradient-to-l from-yellow-400 to-yellow-500";
      else cellClass += " bg-orange-500";
    } else {
      cellClass += " bg-gray-100";
    }

    // Tokens rendering (Ludo King pin/marker style)
    if (here.length > 0) {
      content = (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          {here.map((tok, idx) => {
            const mine = tok.pid === myId;
            const canMove = mine && myMovable?.includes(tok.tokenIdx);
            // Token colors — slot 0 = Red, slot 1 = Yellow
            const fill = tok.slot === 0 ? "#dc2626" : "#eab308";
            const dotFill = tok.slot === 0 ? "#7f1d1d" : "#854d0e";
            return (
              <button
                key={idx}
                disabled={!canMove}
                onClick={() => canMove && onMoveToken?.(tok.tokenIdx)}
                className={`
                  relative pointer-events-auto
                  ${canMove ? "animate-bounce cursor-pointer scale-110 drop-shadow-[0_0_6px_#fde047]" : ""}
                `}
                style={{ width: "95%", height: "95%" }}
                title={`${mine ? 'Your' : 'Opp'} goti (pos ${tok.progress})`}
                data-testid={`token-${tok.slot}-${tok.tokenIdx}`}
              >
                <svg viewBox="0 0 24 32" className="w-full h-full drop-shadow-lg" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <radialGradient id={`grad-${tok.slot}-${idx}-${r}-${c}`} cx="40%" cy="30%" r="60%">
                      <stop offset="0%" stopColor="white" stopOpacity="0.9" />
                      <stop offset="35%" stopColor={fill} stopOpacity="1" />
                      <stop offset="100%" stopColor={dotFill} stopOpacity="1" />
                    </radialGradient>
                  </defs>
                  <path
                    d="M12 1 C5.5 1 1 5.5 1 12 C1 17 6 22 12 31 C18 22 23 17 23 12 C23 5.5 18.5 1 12 1 Z"
                    fill={`url(#grad-${tok.slot}-${idx}-${r}-${c})`}
                    stroke="white"
                    strokeWidth="1.5"
                  />
                  <circle cx="12" cy="11" r="4.2" fill="white" />
                  <circle cx="12" cy="11" r="3" fill={fill} />
                </svg>
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div key={key} className={cellClass}>
        {overlay}
        {content}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex items-center justify-center" data-testid="ludo-board">
      <div
        className="grid gap-0 bg-black/80 p-1 rounded-xl aspect-square border-2 border-yellow-600 shadow-xl"
        style={{
          gridTemplateColumns: "repeat(15, 1fr)",
          gridTemplateRows: "repeat(15, 1fr)",
          width: "min(100%, calc(100vh - 160px))",
          maxWidth: "min(100vw, calc(100vh - 160px))"
        }}
      >
        {cells.map(([r, c]) => renderCell(r, c))}
      </div>
    </div>
  );
}
